<template>
  <a-drawer
    :width="500"
    :visible="visible"
    :unmount-on-close="true"
    :mask-closable="false"
    title="报备消息记录"
    hide-cancel
    ok-text="关闭"
    @ok="handleClose"
    @cancel="handleClose"
  >
    <div class="message-list-container">
      <!-- 空状态 -->
      <a-empty v-if="messageList.length === 0" description="暂无消息记录" />

      <!-- 消息列表 -->
      <div v-else class="message-list">
        <div
          v-for="message in messageList"
          :key="message.id"
          class="message-item"
        >
          <!-- 消息头部：发送者和时间 -->
          <div class="message-header">
            <div class="sender-info">
              <icon-user class="sender-icon" />
              <span class="sender-name">{{ message.username }}</span>
            </div>
            <div class="message-time">
              {{ formatTime(message.times) }}
            </div>
          </div>

          <!-- 消息内容 -->
          <div class="message-content">
            <div v-if="message.content" class="content-text">
              {{ message.content }}
            </div>

            <!-- 消息图片 -->
            <div
              v-if="messageImages(message).length > 0"
              class="content-images"
            >
              <a-image-preview-group>
                <a-space :size="8" wrap>
                  <a-image
                    v-for="(image, index) in messageImages(message)"
                    :key="index"
                    :src="image"
                    width="80"
                    height="80"
                    fit="cover"
                    class="message-image"
                  />
                </a-space>
              </a-image-preview-group>
            </div>
          </div>
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { IconUser } from '@arco-design/web-vue/es/icon';
  import { MessageInfo } from '@/api/report';
  import { stringToArray } from '@/utils/utils';

  interface Props {
    visible: boolean;
    list: Array<MessageInfo>;
  }

  interface Emits {
    (e: 'update:visible', value: boolean): void;
    (e: 'success'): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // 按时间顺序排序消息列表（最新的在下方）
  const messageList = computed(() => {
    return [...props.list].sort((a, b) => {
      return a.createtime - b.createtime;
    });
  });

  // 格式化时间
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    try {
      const date = new Date(timeStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (error) {
      return timeStr;
    }
  };

  // 获取消息图片数组
  const messageImages = (message: MessageInfo) => {
    if (!message.images) return [];
    return stringToArray(message.images);
  };

  // 关闭抽屉
  const handleClose = () => {
    emit('update:visible', false);
  };
</script>

<style lang="less" scoped>
  .message-list-container {
    height: 100%;
    overflow-y: auto;
  }

  .message-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .message-item {
    padding: 16px;
    background-color: var(--color-fill-2);
    border-radius: 8px;
    transition: all 0.3s;

    &:hover {
      background-color: var(--color-fill-3);
    }
  }

  .message-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .sender-info {
    display: flex;
    align-items: center;
    gap: 8px;

    .sender-icon {
      font-size: 16px;
      color: var(--color-text-3);
    }

    .sender-name {
      font-weight: 500;
      color: var(--color-text-1);
      font-size: 14px;
    }
  }

  .message-time {
    font-size: 12px;
    color: var(--color-text-3);
  }

  .message-content {
    .content-text {
      line-height: 1.6;
      color: var(--color-text-2);
      font-size: 14px;
      word-break: break-word;
      white-space: pre-wrap;
      margin-bottom: 12px;
    }

    .content-images {
      margin-top: 8px;

      .message-image {
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          opacity: 0.8;
          transform: scale(1.02);
        }
      }
    }
  }

  // 自定义滚动条样式
  .message-list-container::-webkit-scrollbar {
    width: 6px;
  }

  .message-list-container::-webkit-scrollbar-thumb {
    background-color: var(--color-fill-4);
    border-radius: 3px;

    &:hover {
      background-color: var(--color-fill-5);
    }
  }

  .message-list-container::-webkit-scrollbar-track {
    background-color: transparent;
  }
</style>
